<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, withDefaults } from "vue";

const props = withDefaults(
  defineProps<{
    filter: any;
    activeValue: { min: number; max: number };
  }>(),
  {
    filter: () => ({}),
    activeValue: () => ({ min: 0, max: 0 }),
  }
);

const emit = defineEmits<{
  (e: "update", value: { min: number; max: number }): void;
}>();

const min = computed(() => props.filter.min ?? 0);
const max = computed(() => props.filter.max ?? 100);
const step = computed(() => props.filter.step ?? 0.01);

const isPrice = computed(() => props.filter.unit === '€');
const isSize = computed(() => props.filter.unit === 'ct.');

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref<'min' | 'max' | null>(null);

const localMin = ref(props.activeValue?.min ?? min.value);
const localMax = ref(props.activeValue?.max ?? max.value);

watch(() => props.activeValue, (newVal) => {
  if (!isDragging.value) {
    localMin.value = newVal?.min ?? min.value;
    localMax.value = newVal?.max ?? max.value;
  }
}, { deep: true });

const currentMin = computed({
  get: () => localMin.value,
  set: (val) => {
    localMin.value = Math.min(val, localMax.value);
    emit("update", { min: localMin.value, max: localMax.value });
  }
});

const currentMax = computed({
  get: () => localMax.value,
  set: (val) => {
    localMax.value = Math.max(val, localMin.value);
    emit("update", { min: localMin.value, max: localMax.value });
  }
});

const getPercentage = (value: number) => {
  return ((value - min.value) / (max.value - min.value)) * 100;
};

const minPercent = computed(() => getPercentage(currentMin.value));
const maxPercent = computed(() => getPercentage(currentMax.value));

const handleMouseDown = (type: 'min' | 'max') => {
  isDragging.value = type;
};

const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
  let value = (percentage / 100) * (max.value - min.value) + min.value;
  
  // Snap to step
  if (isPrice.value || isSize.value) {
    value = selectOptions.value.reduce((prev, curr) => {
      return (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
    });
  } else {
    value = Math.round(value / step.value) * step.value;
  }
  
  // Ensure precision to avoid floating point issues
  value = Number(value.toFixed(2));

  if (isDragging.value === 'min') {
    localMin.value = Math.min(value, localMax.value);
  } else {
    localMax.value = Math.max(value, localMin.value);
  }
};

const handleMouseUp = () => {
  if (isDragging.value) {
    emit("update", { min: localMin.value, max: localMax.value });
  }
  isDragging.value = null;
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, { passive: false });
  window.addEventListener('touchend', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('touchmove', handleMouseMove);
  window.removeEventListener('touchend', handleMouseUp);
});

const selectOptions = computed(() => {
  const options = [];
  
  if (isPrice.value) {
    let cur = min.value;
    while (cur <= max.value) {
      options.push(Number(cur.toFixed(2)));
      if (cur < 2000) cur += 100;
      else if (cur < 10000) cur += 500;
      else if (cur < 1000000) cur += 10000;
      else cur += 250000;
    }
  } else if (isSize.value) {
    let curSize = min.value;
    while (curSize <= max.value) {
      options.push(Number(curSize.toFixed(2)));
      if (curSize < 1) {
        curSize += 0.1;
      } else if (curSize < 2) {
        curSize += 0.25;
      } else if (curSize < 3) {
        curSize += 0.25;
      } else if (curSize < 4) {
        curSize += 0.5;
      } else if (curSize < 10) {
        curSize += 1;
      } else if (curSize <= 20) {
        curSize += 5;
      } else {
        curSize += 10;
      }

      curSize = Math.round(curSize * 100) / 100;
    }
  } else {
    const range = max.value - min.value;
    let optStep = step.value;
    if (range > 1000) optStep = 1000;
    else if (range > 100) optStep = 10;
    else if (range > 10) optStep = 1;
    else if (range > 1) optStep = 0.1;

    for (let i = min.value; i <= max.value; i += optStep) {
      options.push(Number(i.toFixed(2)));
    }
  }

  if (options[options.length - 1] !== max.value) {
    options.push(max.value);
  }
  return options;
});

const formatValue = (val: number) => {
  const formatted = val === max.value
    ? '∞'
    : val.toLocaleString('de-DE', {
        minimumFractionDigits: val % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      });
  return formatted + (props.filter.unit ? ' ' + props.filter.unit : '');
};

</script>

<template>
  <div class="diamonds-number-range px-4 pt-4 pb-2 min-w-[300px]">
    <div class="relative w-full h-1 bg-gray-200 rounded-full mb-8" ref="sliderRef">
      <!-- Active range bar -->
      <div 
        class="absolute h-full bg-brand-main rounded-full"
        :style="{ left: minPercent + '%', right: (100 - maxPercent) + '%' }"
      ></div>

      <!-- Min handle -->
      <div 
        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm"
        :style="{ left: minPercent + '%' }"
        @mousedown="handleMouseDown('min')"
        @touchstart.prevent="handleMouseDown('min')"
      >
      </div>

      <!-- Max handle -->
      <div 
        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm"
        :style="{ left: maxPercent + '%' }"
        @mousedown="handleMouseDown('max')"
        @touchstart.prevent="handleMouseDown('max')"
      >
      </div>
    </div>

    <!-- Select boxes -->
    <div class="flex justify-between gap-8 mt-8">
      <div class="flex-1">
        <label class="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">Von</label>
        <select 
          v-model.number="currentMin"
          class="w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        >
          <option v-for="opt in selectOptions.filter(o => o <= currentMax)" :key="'min-'+opt" :value="opt">
            {{ formatValue(opt) }}
          </option>
        </select>
      </div>
      <div class="flex-1 text-right">
        <label class="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bis</label>
        <select 
          v-model.number="currentMax"
          class="w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        >
          <option v-for="opt in selectOptions.filter(o => o >= currentMin)" :key="'max-'+opt" :value="opt">
            {{ formatValue(opt) }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<style scoped>
.diamonds-number-range {
  user-select: none;
}
/* Hide default select arrow for a cleaner look */
select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 12px;
  padding-right: 20px;
}
</style>